import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { AcMenuItem } from "../types/acMenu";

export type DomainOptionItem = {
  value: string;
  key: string;
  abbreviation: string;
};

export type UserSettings = {
  language?: string;
  timeZone?: string;
  dateFormat?: string;
  timeFormat?: string;
  [key: string]: any;
};

export type TenantSettings = {
  dateFormat?: string;
  language?: string;
  timeZone?: string;
  timeFormat?: string;
  sessionTimeoutCountdown?: number;
  sessionTimeOut?: number;
  maxPasswordFailedAttempts?: number;
  autoUnlockDurationMinutes?: number;
  passwordExpiryDays?: number;
  passwordNotifyDaysBeforeExpired?: number;
  passwordHistoryLimit?: number;
  dynamicSettingsJson?: string;
  [key: string]: any;
};

export type Language = "en" | "zhCN" | "jp";
type ConfigStore = {
  language: Language;
  updateLanguage: (data: Language) => void;
  privilege: any[];
  updatePrivilege: (data: any[]) => void;
  /** SideMenu / path auth / dynamic route ∩ — privilege name strings; not persisted */
  menuPrivilege: string[];
  updateMenuPrivilege: (data: string[]) => void;
  /** Filtered AC menus for SideMenu + Router; in-memory only */
  matchedAcMenus: AcMenuItem[];
  updateMatchedAcMenus: (data: AcMenuItem[]) => void;
  userSettings: UserSettings | null;
  updateUserSettings: (data: UserSettings | null) => void;
  tenantSettings: TenantSettings | null;
  updateTenantSettings: (data: TenantSettings | null) => void;
};

export const useConfigStore = create<ConfigStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        language: "en",
        updateLanguage(data: Language) {
          set((state) => {
            state.language = data;
          });
        },
        privilege: [],
        updatePrivilege: (data) => {
          set((state) => {
            state.privilege = data;
          });
        },
        menuPrivilege: [],
        updateMenuPrivilege: (data) => {
          set((state) => {
            state.menuPrivilege = data;
          });
        },
        matchedAcMenus: [],
        updateMatchedAcMenus: (data) => {
          set((state) => {
            state.matchedAcMenus = data;
          });
        },
        userSettings: null,
        updateUserSettings: (data) => {
          set((state) => {
            state.userSettings = data;
          });
        },
        tenantSettings: null,
        updateTenantSettings: (data) => {
          set((state) => {
            state.tenantSettings = data;
          });
        },
      }))
    ),
    {
      name: "global-config-store",
      partialize: (state) => ({
        privilege: state.privilege,
        userSettings: state.userSettings,
        tenantSettings: state.tenantSettings
        // intentionally omit menuPrivilege + matchedAcMenus
      })
    }
  )
);
